import { useState } from 'react';

import { usePagination } from '../../../common/hooks/usePagination';

import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { useTableSort } from '../../../common/hooks/useTableSort';
import {
  useMaterialOpening,
  useMaterialOpeningDetail,
  useMaterialValuationStocks,
} from '../../../hooks/useMaterialCosts';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';
import {
  INITIAL_STOCK_FILTERS,
  REVIEWABLE_VALUATION_STATUSES,
  VALUATION_TABS,
  VALUATION_TEXTS,
} from '../constants/materialValuationConstants';
import { materialOpeningPayload } from '../utils/materialOpeningPayload';
import { useMaterialValuationSources } from './useMaterialValuationSources';

const withoutEmpty = (filters) =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''));

/**
 * Hook de la pantalla de valoración: existencia filtrada, ordenada y paginada en el servidor,
 * fuentes anteriores al costeo y apertura con previsualización obligatoria.
 * @returns {Object} Estado de vista y handlers para la página.
 */
export const useMaterialValuation = () => {
  const [activeTab, setActiveTab] = useState(VALUATION_TABS.stocks);
  const { page, setPage, rowsPerPage, handleRowsPerPageChange } = usePagination();
  const [stockFilters, setStockFilters] = useState(INITIAL_STOCK_FILTERS);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [detailStockId, setDetailStockId] = useState(null);

  const debouncedSearch = useDebouncedValue(stockFilters.search);
  const stockSort = useTableSort(() => setPage(1));
  const sources = useMaterialValuationSources(activeTab);
  const query = useMaterialValuationStocks(
    withoutEmpty({
      ...stockFilters,
      search: debouncedSearch,
      page,
      per_page: rowsPerPage,
      ...stockSort.sortParams,
    })
  );
  const openingDetail = useMaterialOpeningDetail(detailStockId);
  const previewMutation = useMaterialOpening('preview');
  const confirmMutation = useMaterialOpening('confirm');
  const { data: warehouses = [] } = useWarehouses();
  const isSubmitting = previewMutation.isPending || confirmMutation.isPending;
  const isStocksTab = activeTab === VALUATION_TABS.stocks;
  const loadFailure = isStocksTab ? (query.isError ? query.error : null) : sources.error;

  const changeStockFilter = (name, value) => {
    setStockFilters((prev) => ({ ...prev, [name]: value || '' }));
    setPage(1);
  };

  const clearStockFilters = () => {
    setStockFilters(INITIAL_STOCK_FILTERS);
    stockSort.resetSort();
    setPage(1);
  };

  const changeTab = (_, value) => {
    setActiveTab(value);
    sources.reset();
  };

  const openReview = (stock) => {
    setForm({ ...stock, unit_cost_ars: '', source_notes: '', evidence: '' });
    setPreview(null);
    setError('');
  };

  const closeDrawer = () => {
    if (isSubmitting) return;
    setForm(null);
    setPreview(null);
    setError('');
  };

  const changeField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setPreview(null);
  };

  const submit = async () => {
    const isConfirm = Boolean(preview);
    try {
      const mutation = isConfirm ? confirmMutation : previewMutation;
      const result = await mutation.mutateAsync(materialOpeningPayload(form));
      if (isConfirm) {
        setForm(null);
        setPreview(null);
      } else {
        setForm((prev) => ({
          ...prev,
          updated_at: result.expected_updated_at,
          unvalued_quantity: result.unvalued_quantity,
        }));
        setPreview(result);
      }
      setError('');
    } catch (failure) {
      setError(getApiErrorMessage(failure, VALUATION_TEXTS.error));
    }
  };

  const canSubmit = Boolean(
    form && form.unit_cost_ars !== '' && form.source_notes.trim() && form.evidence.trim()
  );

  return {
    activeTab,
    canReview: (stock) => REVIEWABLE_VALUATION_STATUSES.includes(stock.valuation_status),
    canSubmit,
    changeField,
    changeStockFilter,
    changeTab,
    clearStockFilters,
    closeDetail: () => setDetailStockId(null),
    closeDrawer,
    detailError: openingDetail.isError
      ? getApiErrorMessage(openingDetail.error, VALUATION_TEXTS.detailLoadError)
      : '',
    detailOpen: Boolean(detailStockId),
    drawerOpen: Boolean(form),
    error,
    form,
    handlePageChange: (_, value) => setPage(value),
    isLoading: query.isLoading,
    isSubmitting,
    loadError: loadFailure ? getApiErrorMessage(loadFailure, VALUATION_TEXTS.loadError) : '',
    isDetailLoading: openingDetail.isLoading,
    openDetail: (stock) => setDetailStockId(stock.uuid),
    openReview,
    opening: openingDetail.data,
    page,
    rowsPerPage,
    handleRowsPerPageChange,
    preview,
    refetch: isStocksTab ? query.refetch : sources.refetch,
    rows: query.data?.items || [],
    sortStocks: stockSort.toggleSort,
    sources,
    stockFilters,
    stockSort: stockSort.sort,
    submit,
    totalCount: query.data?.total || 0,
    warehouses,
  };
};
